// src/components/layout/AuthLayout.jsx
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useState } from 'react';
import { Instagram, Gift, Star, Users, X, Lock } from 'lucide-react';
import logoPsiando from '@/assets/icon/logo_psiando.jpeg';

// =============================================================================
// LOGO — moldura circular com halo petróleo + borda degradê
// =============================================================================
const Logo = ({ size = 64 }) => {
  const dim = typeof size === 'string' ? parseInt(size, 10) : size;
  // Anel externo um pouco maior para o halo
  const outer = dim + 8;

  return (
    <div
      style={{
        width: outer,
        height: outer,
        borderRadius: '50%',
        padding: 2,
        /* degradê petróleo na borda */
        background: 'linear-gradient(135deg, var(--primary-accent) 0%, var(--primary-main) 60%, var(--primary-dark) 100%)',
        boxShadow: '0 0 0 4px rgba(42,157,143,0.18), 0 8px 24px rgba(13,51,48,0.30)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Círculo branco interno como "passepartout" */}
      <div
        style={{
          width: dim,
          height: dim,
          borderRadius: '50%',
          overflow: 'hidden',
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={logoPsiando}
          alt="Psiando"
          style={{
            width: '88%',
            height: '88%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    </div>
  );
};

// =============================================================================
// FOOTER
// =============================================================================
const FOOTER_SECTIONS = [
  {
    title: 'Serviços',
    links: [
      { label: 'Supervisão Clínica',   to: '/find-supervisor' },
      { label: 'Agenda Digital',       to: null, onTrial: true },
      { label: 'Para Instituições',    to: null, badge: 'Em breve' },
      { label: 'Matching Inteligente', to: '/about' },
    ],
  },
  {
    title: 'Empresa',
    links: [
      { label: 'Sobre Nós', to: '/about'   },
      { label: 'Contato',   to: '/contact' },
      { label: 'Preços',    to: '/pricing' },
      { label: 'Blog',      to: null, badge: 'Em breve' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Política de Privacidade', to: '/privacy'      },
      { label: 'Termos de Uso',           to: '/terms'        },
      { label: 'LGPD',                    to: '/lgpd'         },
      { label: 'Código de Ética',         to: '/codigo-etica' },
    ],
  },
];

const SOCIAL_LINKS = [
  { icon: Instagram, href: 'https://www.instagram.com/psiandosupervisao/', label: 'Instagram' },
];

const ComingSoonBadge = () => (
  <span
    className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full"
    style={{
      backgroundColor: 'rgba(42,157,143,0.20)',
      color: 'var(--secondary-light)',
    }}
  >
    Em breve
  </span>
);

// ─── Dialog de Trial (sem dependência de auth) ────────────────────────────────
function TrialInfoDialog({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #f0faf8' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #e6f7f5, #b2ebe0)' }}
            >
              <Gift size={20} style={{ color: '#1a7a6e' }} />
            </div>
            <div>
              <span
                className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-0.5"
                style={{ backgroundColor: '#e6f7f5', color: '#1a7a6e' }}
              >
                Trial Gratuito por 30 dias
              </span>
              <h3 className="text-base font-bold text-gray-900 leading-tight">
                Desbloqueie Chat Bot/IA e Agenda Digital grátis
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors ml-2 flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Convide <strong className="text-gray-800">10 colegas psicólogos</strong> para se
            cadastrar na Psiando e ganhe acesso completo às funcionalidades bloqueadas por{' '}
            <strong className="text-gray-800">30 dias</strong> — sem cartão de crédito e sem
            compromisso.
          </p>

          <div className="space-y-3">
            {[
              { icon: <Star size={14} />, title: 'Cadastre-se gratuitamente', desc: 'Crie sua conta no plano Multi Psi.' },
              { icon: <Users size={14} />, title: 'Convide 10 colegas', desc: 'Compartilhe seu link exclusivo pelo WhatsApp, e-mail ou redes sociais.' },
              { icon: <Gift size={14} />, title: 'Acesso liberado automaticamente', desc: 'Assim que os 10 convites forem aceitos, o trial de 30 dias começa.' },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'linear-gradient(135deg, #1a7a6e, #2a9d8f)', color: '#fff' }}
                >
                  {step.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{step.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            className="flex items-start gap-2 rounded-xl p-3 text-xs text-gray-500"
            style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
          >
            <Lock size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#94a3b8' }} />
            <span>
              O trial cobre <strong className="text-gray-700">Chat Bot/IA</strong> e{' '}
              <strong className="text-gray-700">Agenda Digital</strong>. Funcionalidades do plano
              base permanecem disponíveis normalmente após o período.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
          <a
            href="https://psiando.com.br/register"
            className="flex-1 text-center font-semibold py-2.5 px-5 rounded-xl text-sm text-white transition-all duration-200 hover:opacity-90 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #1a7a6e, #2a9d8f)' }}
          >
            Quero experimentar grátis
          </a>
          <button
            onClick={onClose}
            className="flex-1 text-center font-semibold py-2.5 px-5 rounded-xl text-sm text-gray-600 border border-gray-200 bg-white transition-all duration-200 hover:bg-gray-50"
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  );
}

const FooterLink = ({ link, onTrialClick }) => {
  if (link.to) {
    return (
      <Link
        to={link.to}
        className="inline-flex items-center text-slate-300 hover:text-white transition-colors text-sm"
      >
        {link.label}
        {link.badge && <ComingSoonBadge />}
      </Link>
    );
  }
  if (link.onTrial) {
    return (
      <button
        type="button"
        onClick={onTrialClick}
        className="inline-flex items-center text-slate-300 hover:text-white transition-colors text-sm"
      >
        {link.label}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center text-slate-400 text-sm">
      {link.label}
      {link.badge && <ComingSoonBadge />}
    </span>
  );
};

const Footer = () => {
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);

  return (
    <>
      <footer className="bg-slate-900 text-white relative z-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center mb-6 gap-4">
              <Logo size={52} />
              <h3 className="text-2xl font-bold">Psiando Global</h3>
            </div>
            <p className="text-slate-300 mb-6 leading-relaxed">
              A primeira plataforma nacional de matching inteligente para supervisão clínica,
              conectando psicólogos com supervisores qualificados.
            </p>
            <div className="flex space-x-4 mb-6">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center transition-colors group"
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--primary-accent)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                >
                  <Icon className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
              ))}
            </div>
            <div className="border-t border-slate-700 pt-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Responsáveis Técnicos</p>
              <p className="text-sm text-slate-300 leading-relaxed">
                Hermelany Mª Luna Antunes<br />
                <span className="text-slate-400 text-xs">CRP: 02/26332</span>
              </p>
              <p className="text-sm text-slate-300 leading-relaxed mt-2">
                Victória Caroline de França Torres<br />
                <span className="text-slate-400 text-xs">CRP: 02/27470</span>
              </p>
            </div>
          </div>

          {/* Colunas */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {FOOTER_SECTIONS.map(({ title, links }) => (
                <div key={title}>
                  <h4 className="text-lg font-semibold mb-6 text-white">{title}</h4>
                  <ul className="space-y-3">
                    {links.map((link) => (
                      <li key={link.label}><FooterLink link={link} onTrialClick={() => setTrialDialogOpen(true)} /></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Psiando. Todos os direitos reservados.
          </p>
          <div className="flex items-center space-x-6 mt-4 md:mt-0">
            <span className="text-slate-400 text-sm">Feito com ❤️ no Brasil</span>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--primary-accent)' }} />
            <span className="text-slate-400 text-sm">Sistema seguro LGPD</span>
          </div>
        </div>
      </div>
    </div>
      </footer>

      {trialDialogOpen && (
        <TrialInfoDialog onClose={() => setTrialDialogOpen(false)} />
      )}
    </>
  );
};

// =============================================================================
// AUTH NAVIGATION
// =============================================================================
const AuthNavigation = () => {
  const location   = useLocation();
  const isLogin    = location.pathname.includes('login');
  const isRegister = location.pathname.includes('register');
  const isForgot   = location.pathname.includes('forgot-password');

  const linkStyle   = { color: 'var(--primary-main)', fontWeight: 600, transition: 'color 0.15s' };
  const hoverIn  = e => (e.currentTarget.style.color = 'var(--primary-accent)');
  const hoverOut = e => (e.currentTarget.style.color = 'var(--primary-main)');

  if (isLogin) return (
    <div className="text-center space-y-3">
      <p className="text-sm text-gray-600">
        Ainda não tem uma conta?{' '}
        <Link to="/auth/register" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
          Cadastre-se gratuitamente
        </Link>
      </p>
      <Link to="/auth/forgot-password" className="block text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
        Esqueceu sua senha?
      </Link>
    </div>
  );

  if (isRegister) return (
    <div className="text-center">
      <p className="text-sm text-gray-600">
        Já tem uma conta?{' '}
        <Link to="/auth/login" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
          Faça login
        </Link>
      </p>
    </div>
  );

  if (isForgot) return (
    <div className="text-center">
      <p className="text-sm text-gray-600">
        Lembrou da senha?{' '}
        <Link to="/auth/login" style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
          Fazer login
        </Link>
      </p>
    </div>
  );

  return (
    <div className="text-center space-y-3">
      <Link to="/auth/login" className="block text-sm font-semibold" style={{ color: 'var(--primary-main)' }}>Fazer Login</Link>
      <Link to="/auth/register" className="block text-sm text-gray-600 hover:text-gray-700 font-medium transition-colors">Criar Conta</Link>
    </div>
  );
};

// =============================================================================
// METADADOS POR PÁGINA
// =============================================================================
const getPageInfo = (pathname) => {
  if (pathname.includes('login'))
    return {
      title:           'Bem-vindo de volta!',
      subtitle:        'Entre na sua conta para continuar sua jornada de supervisão',
      backgroundImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1920&h=1080&fit=crop',
    };
  if (pathname.includes('register'))
    return {
      title:           'Junte-se à revolução da supervisão clínica',
      subtitle:        'Crie sua conta e transforme sua prática profissional',
      backgroundImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1920&h=1080&fit=crop',
    };
  if (pathname.includes('forgot-password'))
    return {
      title:           'Recuperar sua senha',
      subtitle:        'Enviaremos instruções seguras para o seu email',
      backgroundImage: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=1920&h=1080&fit=crop',
    };
  return {
    title:           'Psiando',
    subtitle:        'Plataforma Global de Supervisão Clínica',
    backgroundImage: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1920&h=1080&fit=crop',
  };
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export const AuthLayout = () => {
  const location = useLocation();
  const pageInfo = getPageInfo(location.pathname);

  useEffect(() => {
    document.body.classList.add('auth-page');
    return () => document.body.classList.remove('auth-page');
  }, []);

  return (
    <div className="min-h-screen flex flex-col">

      {/* ── Fundo fotográfico com overlay petróleo ── */}
      <div className="absolute inset-0 z-0">
        <img className="h-full w-full object-cover" src={pageInfo.backgroundImage} alt="" aria-hidden="true" />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg,
              rgba(13,51,48,0.60) 0%,
              rgba(26,92,88,0.70) 50%,
              rgba(42,157,143,0.50) 100%)`,
          }}
        />
      </div>

      {/* ── Decorativos ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'rgba(168,218,220,0.08)' }} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'rgba(42,157,143,0.08)' }} />
      </div>

      {/* ── Conteúdo ── */}
      <main className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="w-full max-w-md lg:max-w-2xl xl:max-w-4xl 2xl:max-w-5xl">

          {/* Marca */}
          <div className="text-center mb-10">
            <Link to="/" className="inline-flex flex-col items-center gap-4 group">
              {/* Logo com moldura circular petróleo */}
              <div
                style={{
                  transition: 'transform 0.3s ease',
                }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.06)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <Logo size={72} />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white drop-shadow-lg">
                Psiando
              </h1>
              <p className="text-lg text-white/90 font-medium drop-shadow-sm">
                Supervisão Clínica &amp; Agenda Digital
              </p>
            </Link>
          </div>

          {/* Título da página */}
          <div className="mb-10 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight drop-shadow-lg">
              {pageInfo.title}
            </h2>
            <p className="text-xl text-white/90 leading-relaxed drop-shadow-sm max-w-2xl mx-auto">
              {pageInfo.subtitle}
            </p>
          </div>

          {/* ── Card do formulário ── */}
          <div
            className="relative rounded-3xl shadow-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.35)',
              padding: 'clamp(1.5rem, 4vw, 3rem)',
            }}
          >
            {/* Linha decorativa petróleo no topo do card */}
            <div
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                background: 'linear-gradient(90deg, var(--primary-dark) 0%, var(--primary-accent) 50%, var(--secondary-light) 100%)',
              }}
            />

            {/* Brilho sutil interno */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />

            <div className="space-y-6 relative z-10">
              <Outlet />
            </div>

            {/* Navegação entre login/registro */}
            <div
              className="mt-7 pt-7 relative z-10"
              style={{ borderTop: '1px solid var(--gray-200)' }}
            >
              <AuthNavigation />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuthLayout;